/**
 * Validate Upload - Type Definitions
 * Bill upload validation workflow (list, view, approve/reject)
 */

export type ValidateUploadStatusFlag = 0 | 1 | 2;
export type BillApprovalListType = 'pending' | 'approved' | 'rejected';

export interface ValidateUploadEditableField {
  value: string | number;
  editable: boolean;
  mandatory?: boolean;
}

export interface ValidateUploadBillDetails {
  bill_number?: ValidateUploadEditableField;
  bill_date?: ValidateUploadEditableField;
  bill_value?: ValidateUploadEditableField;
  [key: string]: ValidateUploadEditableField | undefined;
}

export interface ValidateUploadLineItemSku {
  value: string;
  sku_id: number;
  sku_name: string;
  editable: boolean;
  mandatory?: boolean;
}

export interface ValidateUploadLineItem {
  item_id: number;
  sku: ValidateUploadLineItemSku;
  quantity: ValidateUploadEditableField;
  points_calculated: number;
}

export interface ValidateUploadSkuOption {
  id: number;
  sku_code: string;
  brand_name: string;
  price: number;
  default_points: number;
}

export interface ValidateUploadRecord {
  id: number;
  bill_order_id: number;
  bill_id: string;
  user_id: number;
  username: string;
  mobile: string;
  email?: string;
  bill_number: string;
  bill_date: string;
  bill_value: number;
  bill_details?: ValidateUploadBillDetails;
  campaign_name: string;
  bill_type_name: string;
  sample_invoice_url?: string;
  serial_number?: string;
  per_sku_points: number;
  total_points: number;
  qty: number;
  brand_name: string;
  uploaded_date: string;
  updated_date?: string;
  agent_date?: string;
  agent_status_flag: ValidateUploadStatusFlag;
  status_text?: string;
  status?: number;
  image_urls: string;
  line_items?: ValidateUploadLineItem[];
  sku_options?: ValidateUploadSkuOption[];
  reason?: string;
  rejection_reason?: string;
  rejection_id?: number | string;
  remarks?: string;
  approver_name?: string;
}

export interface ValidateUploadCounts {
  total_count: number;
  pending_count: number;
  approved_count: number;
  rejected_count: number;
}

export interface ValidateUploadRejectReason {
  id: number;
  reason: string;
  module_type?: number;
  module_type_name?: string;
}

export interface ValidateUploadListFilters {
  search?: string;
  status?: string;
  month?: string;
  year?: string;
  page?: number;
  pageSize?: number;
  bill_id?: string | number;
  /** @deprecated Use bill_id */
  bill_order_id?: string | number;
}

export interface ValidateUploadPagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ValidateUploadListResponse {
  data: ValidateUploadRecord[];
  counts: ValidateUploadCounts;
  total: number;
  pagination: ValidateUploadPagination;
}

export type BillApprovalEditableFieldsPayload = Record<string, string | number>;

export interface BillApprovalLineItemPayload {
  item_id: number;
  sku: string;
  quantity: number;
}

export interface BillApprovalApprovePayload {
  bill_order_id: number;
  remarks?: string;
  line_items?: BillApprovalLineItemPayload[];
}

export interface BillApprovalRejectPayload {
  bill_order_id: number;
  rejection_reason_id: number;
  remarks: string;
  line_items?: BillApprovalLineItemPayload[];
}

/** @deprecated Use BillApprovalApprovePayload / BillApprovalRejectPayload */
export interface ValidateUploadStatusPayload {
  bill_id: string;
  status: '1' | '2';
  rejection_id?: string;
  rejection_reason?: string;
}

/** @deprecated Use ValidateUploadEditableField */
export type ValidateUploadBillField = ValidateUploadEditableField;
